import React, { useRef, useState } from "react";
import { View, TextInput, useColorScheme } from "react-native";

interface OTPInputProps {
  length?: number;
  onComplete?: (otp: string) => void;
  onChange?: (otp: string) => void;
  onInvalidInput?: () => void;
  className?: string;
  appearance?: "light" | "dark" | "glass" | "auto";
}

export function OTPInput({
  length = 6,
  onComplete,
  onChange,
  onInvalidInput,
  className = "",
  appearance = "auto",
}: OTPInputProps) {
  const [otp, setOtp] = useState<string[]>(new Array(length).fill(""));
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const inputRefs = useRef<TextInput[]>([]);
  const colorScheme = useColorScheme();
  const effectiveAppearance =
    appearance === "auto"
      ? colorScheme === "dark"
        ? "dark"
        : "light"
      : appearance;
  const isDark = effectiveAppearance === "dark";
  const isGlass = effectiveAppearance === "glass";

  const handleChange = (value: string, index: number) => {
    // Normalize typed and pasted values to digits before updating the cells.
    const normalized = value.replace(/\D/g, "");

    // Reject non-numeric input, but allow an empty value for backspace.
    if (value && !normalized) {
      onInvalidInput?.();
      return;
    }

    // check if dealing with paste
    if (normalized.length > 1) {
      if (normalized?.length !== length) {
        onInvalidInput?.();
        return;
      }

      const pastedOtp = normalized.split("");
      setOtp(pastedOtp);
      setActiveIndex(pastedOtp.length - 1);
      onChange?.(normalized);
      onComplete?.(normalized);

      return;
    }

    const newOtp = [...otp];
    newOtp[index] = normalized;
    setOtp(newOtp);
    onChange?.(newOtp.join(""));

    const otpString = newOtp.join("");

    if (normalized && index < length - 1) {
      setActiveIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }

    if (otpString.length === length) {
      onComplete?.(otpString);
    }
  };

  const handleKeyPress = (key: string, index: number) => {
    if (key === "Backspace" && !otp[index] && index > 0) {
      setActiveIndex(index - 1);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleFocus = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <View className={`flex flex-row justify-center gap-3 ${className}`}>
      {otp.map((digit, index) => (
        <TextInput
          key={index}
          id={`otp-${index}`}
          ref={(ref) => {
            if (ref) inputRefs.current[index] = ref;
          }}
          value={digit}
          onChangeText={(value) => handleChange(value, index)}
          onKeyPress={({ nativeEvent }) =>
            handleKeyPress(nativeEvent.key, index)
          }
          onFocus={() => handleFocus(index)}
          autoComplete="off"
          autoCorrect={false}
          keyboardType="numeric"
          maxLength={length}
          selectTextOnFocus
          testID={`otp-input-${index}`}
          style={[
            { width: `${Math.floor(100 / length)}%` },
            isGlass
              ? {
                  backgroundColor:
                    activeIndex === index
                      ? "rgba(255, 255, 255, 0.08)"
                      : "transparent",
                  borderColor:
                    activeIndex === index
                      ? "rgba(255, 239, 225, 0.28)"
                      : "rgba(255, 239, 225, 0.15)",
                  borderTopColor:
                    activeIndex === index
                      ? "rgba(255, 249, 244, 0.44)"
                      : "rgba(255, 244, 236, 0.28)",
                  borderBottomColor: "rgba(255, 225, 207, 0.10)",
                  color: "rgba(255, 255, 255, 0.94)",
                }
              : null,
          ]}
          className={`flex aspect-square max-w-14 items-center justify-center rounded-2xl border text-center text-2xl font-semibold outline-none ${
            isGlass
              ? "text-white"
              : activeIndex === index
                ? isDark
                  ? "border-primary bg-primary/10 text-white"
                  : "border-primary bg-primary/10 text-foreground"
                : isDark
                  ? "border-white/20 bg-white/5 text-white"
                  : "border-border text-foreground bg-white"
          }`}
        />
      ))}
    </View>
  );
}

export default OTPInput;
